import type { DataService } from './api';
import type {
  Worker, Admin, DailyCheckin, CheckinInput, Alert, AlertFilter,
  AdminReply, Notice, NoticeInput, NoticeRead, AdminNote, AdminNoteInput,
  DashboardData, CheckinRevision, MonthlyReportData,
  WorkerSignupInput, WorkerSignupRequest, ChangeAdminPasswordInput, RepairWorkerFieldsResult,
  PrivacyConsent, AttentionWorkerItem, DashboardMessageItem, WorkerSummaryItem,
  ResolveAttentionInput, MarkAttentionMonitoringInput, AttendanceRecord,
  AttendanceUpdateInput, AttendanceOverviewData, AttendanceOverviewRow, WorkerHomeData,
  AdminMessagesData, WorkerDetailData
} from './types';
import { PRIVACY_CONSENT_VERSION } from './types';
import { getTodayString, isWorkerEligibleForStatsOnDate, normalizeDateString } from '../utils/date';
import { calculateAverage, checkAlertConditions } from '../utils/score';
import { hashPin, verifyPin } from '../utils/hash';

const PRIVACY_CONSENT_SNAPSHOT = JSON.stringify({
  privacy: '취업 후 적응지원을 위해 이름, 취업처, 수행 직무, 취업일자, 일일 체크 기록을 수집합니다.',
  sensitive: '기분, 몸 상태, 도움 필요 여부 등 민감할 수 있는 정보가 포함될 수 있습니다.',
  retention: '기록은 기관 내부 기록관리 기준에 따라 별도 파기 결정 전까지 보관될 수 있습니다.'
});

function normalizeWorkerForStorage(worker: Worker): Worker {
  return {
    ...worker,
    employment_start_date: normalizeDateString(worker.employment_start_date || ''),
    employment_end_date: normalizeDateString(worker.employment_end_date || ''),
  };
}

function normalizeSignupRequestForStorage(request: WorkerSignupRequest): WorkerSignupRequest {
  return {
    ...request,
    employment_start_date: normalizeDateString(request.employment_start_date || ''),
  };
}

export class LocalStorageService implements DataService {
  private getStorageItem<T>(key: string, defaultValue: T): T {
    const stored = localStorage.getItem(key);
    if (!stored) return defaultValue;
    try {
      return JSON.parse(stored) as T;
    } catch {
      return defaultValue;
    }
  }

  private setStorageItem<T>(key: string, value: T): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  // 인증
  async workerLogin(name: string, pin: string): Promise<Worker> {
    const workers = this.getStorageItem<Worker[]>('workfit_workers', []);
    const worker = workers.find(w => w.name === name);
    if (!worker) {
      const err = new Error('등록되지 않은 이름입니다.');
      (err as any).code = 'name_not_found';
      throw err;
    }
    const isMatch = worker.pin_hash ? await verifyPin(pin, worker.pin_hash) : pin === '1234';
    if (!isMatch) {
      const err = new Error('비밀번호가 올바르지 않습니다.');
      (err as any).code = 'invalid_pin';
      throw err;
    }
    if (worker.status !== 'active' || worker.employment_status !== '취업 중') {
      const err = new Error('현재 이용 가능한 취업 상태가 아닙니다. 복지관 고용지원팀에 문의해 주세요.');
      (err as any).code = 'inactive_worker';
      throw err;
    }
    return worker;
  }

  async adminLogin(loginId: string, password: string): Promise<Admin | null> {
    let admins = this.getStorageItem<Admin[]>('workfit_admins', []);
    
    // 개발 목업 모드에서만 초기 관리자 계정을 자동 생성합니다.
    if (
      import.meta.env.DEV &&
      import.meta.env.VITE_USE_MOCK_DATA === 'true' &&
      admins.length === 0
    ) {
      const now = new Date().toISOString();
      const mockAdminsInfo = [
        { id: 'A001', name: '관리자1', login: 'enkidua@seoulcbid.or.kr' },
        { id: 'A002', name: '관리자2', login: 'admin2' },
        { id: 'A003', name: '관리자3', login: 'admin3' },
        { id: 'A004', name: '관리자4', login: 'admin4' },
        { id: 'A005', name: '관리자5', login: 'admin5' },
        { id: 'A006', name: '관리자6', login: 'admin6' }
      ];
      
      admins = [];
      for (const info of mockAdminsInfo) {
        const password_hash = await hashPin('admin1234');
        admins.push({
          admin_id: info.id,
          name: info.name,
          login_id: info.login,
          password_hash,
          status: 'active',
          must_change_password: true,
          last_password_changed_at: '',
          created_at: now,
          updated_at: now
        });
      }
      this.setStorageItem('workfit_admins', admins);
    }
    
    const admin = admins.find(a => a.login_id === loginId && a.status === 'active');
    if (!admin) return null;
    
    const isMatch = admin.password_hash ? await verifyPin(password, admin.password_hash) : password === 'admin1234';
    if (!isMatch) return null;
    
    const cleanAdmin = { ...admin };
    delete (cleanAdmin as any).password_hash;
    return cleanAdmin;
  }

  // 취업자 관리
  async getWorkers(): Promise<Worker[]> {
    return this.getStorageItem<Worker[]>('workfit_workers', []).map(normalizeWorkerForStorage);
  }

  async getWorker(workerId: string): Promise<Worker | null> {
    const workers = await this.getWorkers();
    return workers.find(w => w.worker_id === workerId) || null;
  }

  async createWorker(data: Omit<Worker, 'worker_id' | 'created_at' | 'updated_at'>): Promise<Worker> {
    const workers = await this.getWorkers();
    const nextId = `W${String(workers.length + 1).padStart(3, '0')}`;
    const now = new Date().toISOString();
    const newWorker: Worker = {
      ...data,
      employment_start_date: normalizeDateString(data.employment_start_date || ''),
      employment_end_date: normalizeDateString(data.employment_end_date || ''),
      worker_id: nextId,
      created_at: now,
      updated_at: now
    };
    workers.push(newWorker);
    this.setStorageItem('workfit_workers', workers);
    return newWorker;
  }

  async updateWorker(workerId: string, data: Partial<Worker> & { pin?: string }): Promise<Worker> {
    const workers = await this.getWorkers();
    const index = workers.findIndex(w => w.worker_id === workerId);
    if (index === -1) throw new Error('취업자를 찾을 수 없습니다.');
    
    const { pin, ...updateFields } = data;
    const updated: Worker = {
      ...workers[index],
      ...updateFields,
      employment_start_date: Object.prototype.hasOwnProperty.call(updateFields, 'employment_start_date')
        ? normalizeDateString(updateFields.employment_start_date || '')
        : normalizeDateString(workers[index].employment_start_date || ''),
      employment_end_date: Object.prototype.hasOwnProperty.call(updateFields, 'employment_end_date')
        ? normalizeDateString(updateFields.employment_end_date || '')
        : normalizeDateString(workers[index].employment_end_date || ''),
      updated_at: new Date().toISOString()
    };

    if (pin && pin.trim() !== '') {
      updated.pin_hash = await hashPin(pin.trim());
    }

    workers[index] = updated;
    this.setStorageItem('workfit_workers', workers);
    return updated;
  }

  async deleteWorker(workerId: string, terminationDate?: string, reason?: string): Promise<void> {
    const defaultDate = terminationDate || getTodayString();
    const defaultReason = reason || '퇴사';
    await this.terminateWorker(workerId, defaultDate, defaultReason);
  }

  async terminateWorker(workerId: string, terminationDate: string, reason: string): Promise<void> {
    await this.updateWorker(workerId, {
      status: 'inactive',
      employment_status: '퇴사',
      employment_end_date: terminationDate,
      termination_reason: reason
    } as any);
  }

  async createWorkerSignupRequest(data: WorkerSignupInput): Promise<WorkerSignupRequest> {
    if (!data.name.trim() || !data.pin.trim() || !data.current_company.trim()) {
      throw new Error('이름, 비밀번호, 현재 취업처를 입력해 주세요.');
    }
    if (!data.privacy_required_agreed || !data.sensitive_required_agreed) {
      const err = new Error('개인정보 수집·이용 동의가 필요합니다.');
      (err as any).code = 'privacy_consent_required';
      throw err;
    }
    const requests = this.getStorageItem<WorkerSignupRequest[]>('workfit_worker_signup_requests', []);
    const duplicate = requests.some(r =>
      r.status === 'pending' &&
      r.name.trim() === data.name.trim() &&
      r.current_company.trim() === data.current_company.trim()
    );
    if (duplicate) {
      throw new Error('이미 같은 이름과 취업처로 접수된 등록 신청이 있습니다.');
    }

    const now = new Date().toISOString();
    const request: WorkerSignupRequest = {
      request_id: `WSR-${Date.now()}`,
      name: data.name.trim(),
      pin_hash: await hashPin(data.pin),
      current_company: data.current_company.trim(),
      employment_start_date: normalizeDateString(data.employment_start_date),
      job_type: data.job_type?.trim() || '',
      phone_optional: data.phone_optional?.trim() || '',
      privacy_required_agreed: data.privacy_required_agreed,
      sensitive_required_agreed: data.sensitive_required_agreed,
      contact_optional_agreed: Boolean(data.contact_optional_agreed),
      consent_version: data.consent_version || PRIVACY_CONSENT_VERSION,
      consent_at: now,
      status: 'pending',
      admin_memo: '',
      reviewed_by: '',
      reviewed_at: '',
      converted_worker_id: '',
      created_at: now,
      updated_at: now
    };
    requests.push(request);
    this.setStorageItem('workfit_worker_signup_requests', requests);

    const consents = this.getStorageItem<PrivacyConsent[]>('workfit_privacy_consents', []);
    consents.push({
      consent_id: `PC-${Date.now()}`,
      user_type: 'signup_request',
      user_id: '',
      request_id: request.request_id,
      name: request.name,
      consent_version: request.consent_version || PRIVACY_CONSENT_VERSION,
      privacy_required_agreed: true,
      sensitive_required_agreed: true,
      contact_optional_agreed: Boolean(request.contact_optional_agreed),
      consent_text_snapshot: PRIVACY_CONSENT_SNAPSHOT,
      agreed_at: now,
      created_at: now
    });
    this.setStorageItem('workfit_privacy_consents', consents);
    return request;
  }

  async getWorkerSignupRequests(status?: 'pending' | 'approved' | 'rejected'): Promise<WorkerSignupRequest[]> {
    const requests = this.getStorageItem<WorkerSignupRequest[]>('workfit_worker_signup_requests', []);
    const filtered = status ? requests.filter(r => r.status === status) : requests;
    return filtered.map(normalizeSignupRequestForStorage).sort((a, b) => b.created_at.localeCompare(a.created_at));
  }

  async approveWorkerSignupRequest(requestId: string, adminId: string, adminMemo = ''): Promise<Worker> {
    const requests = this.getStorageItem<WorkerSignupRequest[]>('workfit_worker_signup_requests', []);
    const index = requests.findIndex(r => r.request_id === requestId);
    if (index === -1) throw new Error('등록 신청을 찾을 수 없습니다.');
    if (requests[index].status !== 'pending') throw new Error('이미 처리된 등록 신청입니다.');

    const workers = await this.getWorkers();
    const nextId = `W${String(workers.length + 1).padStart(3, '0')}`;
    const now = new Date().toISOString();
    const request = requests[index];
    const employmentStartDate = normalizeDateString(request.employment_start_date || '');
    if (!employmentStartDate) {
      throw new Error('등록 신청의 취업일자가 없어 승인할 수 없습니다.');
    }
    const worker: Worker = {
      worker_id: nextId,
      name: request.name,
      pin_hash: request.pin_hash,
      current_company: request.current_company,
      job_type: request.job_type,
      employment_start_date: employmentStartDate,
      employment_end_date: '',
      employment_status: '취업 중',
      termination_reason: '',
      phone_optional: request.phone_optional,
      status: 'active',
      created_at: now,
      updated_at: now
    };
    workers.push(worker);
    requests[index] = {
      ...request,
      status: 'approved',
      admin_memo: adminMemo,
      reviewed_by: adminId,
      reviewed_at: now,
      converted_worker_id: nextId,
      updated_at: now
    };
    const consents = this.getStorageItem<PrivacyConsent[]>('workfit_privacy_consents', []);
    const updatedConsents = consents.map((consent) =>
      consent.request_id === requestId ? { ...consent, user_type: 'worker' as const, user_id: nextId } : consent
    );
    this.setStorageItem('workfit_workers', workers);
    this.setStorageItem('workfit_worker_signup_requests', requests);
    this.setStorageItem('workfit_privacy_consents', updatedConsents);
    return worker;
  }

  async rejectWorkerSignupRequest(requestId: string, adminId: string, adminMemo = ''): Promise<WorkerSignupRequest> {
    const requests = this.getStorageItem<WorkerSignupRequest[]>('workfit_worker_signup_requests', []);
    const index = requests.findIndex(r => r.request_id === requestId);
    if (index === -1) throw new Error('등록 신청을 찾을 수 없습니다.');
    const now = new Date().toISOString();
    requests[index] = {
      ...requests[index],
      status: 'rejected',
      admin_memo: adminMemo,
      reviewed_by: adminId,
      reviewed_at: now,
      updated_at: now
    };
    this.setStorageItem('workfit_worker_signup_requests', requests);
    return requests[index];
  }

  async checkWorkerSignupStatus(name: string, pin: string): Promise<WorkerSignupRequest | null> {
    const pinHash = await hashPin(pin);
    const requests = this.getStorageItem<WorkerSignupRequest[]>('workfit_worker_signup_requests', []);
    return requests
      .map(normalizeSignupRequestForStorage)
      .filter(r => r.name.trim() === name.trim() && r.pin_hash === pinHash)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0] || null;
  }

  // 관리자 관리
  async getAdmins(): Promise<Admin[]> {
    return this.getStorageItem<Admin[]>('workfit_admins', []);
  }

  async createAdmin(data: Omit<Admin, 'admin_id' | 'created_at' | 'updated_at'>): Promise<Admin> {
    const admins = await this.getAdmins();
    const nextId = `A${String(admins.length + 1).padStart(3, '0')}`;
    const now = new Date().toISOString();
    const newAdmin: Admin = {
      ...data,
      admin_id: nextId,
      created_at: now,
      updated_at: now
    };
    admins.push(newAdmin);
    this.setStorageItem('workfit_admins', admins);
    return newAdmin;
  }

  async updateAdmin(adminId: string, data: Partial<Admin>): Promise<Admin> {
    const admins = this.getStorageItem<Admin[]>('workfit_admins', []);
    const index = admins.findIndex(a => a.admin_id === adminId);
    if (index === -1) throw new Error('관리자를 찾을 수 없습니다.');
    const updated: Admin = {
      ...admins[index],
      ...data,
      updated_at: new Date().toISOString()
    };
    admins[index] = updated;
    this.setStorageItem('workfit_admins', admins);
    return updated;
  }

  async changeAdminPassword(data: ChangeAdminPasswordInput): Promise<void> {
    const admins = this.getStorageItem<Admin[]>('workfit_admins', []);
    const index = admins.findIndex(a => a.admin_id === data.admin_id);
    if (index === -1) throw new Error('관리자를 찾을 수 없습니다.');
    
    const admin = admins[index];
    if (!data.current_password) {
      const err = new Error('현재 비밀번호를 입력해 주세요.');
      (err as any).code = 'current_password_required';
      throw err;
    }
    const isMatch = admin.password_hash ? await verifyPin(data.current_password, admin.password_hash) : data.current_password === 'admin1234';
    if (!isMatch) {
      const err = new Error('현재 비밀번호가 올바르지 않습니다.');
      (err as any).code = 'invalid_current_password';
      throw err;
    }
    
    if (data.new_password.length < 8) {
      throw new Error('비밀번호는 최소 8자 이상이어야 합니다.');
    }
    
    const newHash = await hashPin(data.new_password);
    if (admin.password_hash === newHash) {
      throw new Error('새 비밀번호는 기존 비밀번호와 달라야 합니다.');
    }
    
    admins[index] = {
      ...admin,
      password_hash: newHash,
      must_change_password: false,
      last_password_changed_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    this.setStorageItem('workfit_admins', admins);
  }

  // 체크인
  async getCheckins(workerId: string, month?: string): Promise<DailyCheckin[]> {
    const checkins = this.getStorageItem<DailyCheckin[]>('workfit_checkins', []);
    let filtered = workerId.trim() ? checkins.filter(c => c.worker_id === workerId) : checkins;
    if (month) {
      filtered = filtered.filter(c => c.checkin_date.startsWith(month));
    }
    return filtered;
  }

  async getCheckinByDate(workerId: string, date: string): Promise<DailyCheckin | null> {
    const checkins = this.getStorageItem<DailyCheckin[]>('workfit_checkins', []);
    const checkin = checkins.find(c => c.worker_id === workerId && c.checkin_date === date);
    return checkin || null;
  }

  async getTodayCheckin(workerId: string): Promise<DailyCheckin | null> {
    return this.getCheckinByDate(workerId, getTodayString());
  }

  async createCheckin(data: CheckinInput): Promise<DailyCheckin> {
    const checkins = this.getStorageItem<DailyCheckin[]>('workfit_checkins', []);
    const scores = [data.q1_mood, data.q2_work, data.q3_relationship, data.q4_health, data.q5_need_help, data.q6_overall];
    const average_score = calculateAverage(scores);
    const now = new Date().toISOString();
    const checkin_id = `C-${data.checkin_date}-${data.worker_id}`;
    const existingIndex = checkins.findIndex(c => c.worker_id === data.worker_id && c.checkin_date === data.checkin_date);

    const newCheckin: DailyCheckin = {
      checkin_id: existingIndex >= 0 ? checkins[existingIndex].checkin_id : checkin_id,
      worker_id: data.worker_id,
      checkin_date: data.checkin_date,
      q1_mood: data.q1_mood,
      q2_work: data.q2_work,
      q3_relationship: data.q3_relationship,
      q4_health: data.q4_health,
      q5_need_help: data.q5_need_help,
      q6_overall: data.q6_overall,
      average_score,
      message_to_admin: data.message_to_admin || '',
      message_tags: data.message_tags ? data.message_tags.join(',') : '',
      is_edited: existingIndex >= 0,
      original_checkin_id: existingIndex >= 0 ? checkins[existingIndex].checkin_id : '',
      created_at: existingIndex >= 0 ? checkins[existingIndex].created_at : now,
      updated_at: now
    };

    if (existingIndex >= 0) {
      const revisions = this.getStorageItem<CheckinRevision[]>('workfit_checkin_revisions', []);
      revisions.push({
        revision_id: `R-${Date.now()}`,
        checkin_id: checkins[existingIndex].checkin_id,
        worker_id: data.worker_id,
        checkin_date: data.checkin_date,
        previous_payload_json: JSON.stringify(checkins[existingIndex]),
        new_payload_json: JSON.stringify(newCheckin),
        edited_by: data.worker_id,
        edited_at: now
      });
      this.setStorageItem('workfit_checkin_revisions', revisions);
      checkins[existingIndex] = newCheckin;
    } else {
      checkins.push(newCheckin);
    }
    this.setStorageItem('workfit_checkins', checkins);

    // 알림 조건 검사 및 생성
    const previousCheckins = checkins
      .filter(c => c.worker_id === data.worker_id && c.checkin_date < data.checkin_date)
      .sort((a, b) => b.checkin_date.localeCompare(a.checkin_date));

    const alertTypes = checkAlertConditions(newCheckin, previousCheckins);
    if (alertTypes.length > 0) {
      const alerts = this.getStorageItem<Alert[]>('workfit_alerts', []);
      alertTypes.forEach((type, index) => {
        alerts.push({
          alert_id: `A-${data.checkin_date}-${data.worker_id}-${index}-${Date.now().toString().slice(-4)}`,
          worker_id: data.worker_id,
          checkin_id,
          alert_date: data.checkin_date,
          alert_type: type,
          previous_score: previousCheckins.length > 0 ? previousCheckins[0].average_score : 0,
          current_score: average_score,
          score_drop: previousCheckins.length > 0 ? previousCheckins[0].average_score - average_score : 0,
          reason: this.getAlertReason(type),
          status: 'new',
          created_at: now,
          resolved_at: '',
          admin_memo: ''
        });
      });
      this.setStorageItem('workfit_alerts', alerts);
    }

    // 관리자 메시지가 있을 경우 replies 추가
    if (data.message_to_admin) {
      const replies = this.getStorageItem<AdminReply[]>('workfit_replies', []);
      replies.push({
        reply_id: `RPL-${Date.now()}`,
        checkin_id,
        worker_id: data.worker_id,
        admin_id: '',
        worker_message: data.message_to_admin,
        reply_text: '',
        message_status: '미확인',
        worker_read_at: '',
        created_at: now,
        updated_at: now
      });
      this.setStorageItem('workfit_replies', replies);
    }

    return newCheckin;
  }

  async updateCheckin(checkinId: string, data: CheckinInput): Promise<DailyCheckin> {
    const checkins = this.getStorageItem<DailyCheckin[]>('workfit_checkins', []);
    const index = checkins.findIndex(c => c.checkin_id === checkinId);
    if (index === -1) throw new Error('체크인을 찾을 수 없습니다.');

    const current = checkins[index];
    const now = new Date().toISOString();

    // 수정 이력 추가
    const revisions = this.getStorageItem<CheckinRevision[]>('workfit_checkin_revisions', []);
    revisions.push({
      revision_id: `R-${Date.now()}`,
      checkin_id: checkinId,
      worker_id: current.worker_id,
      checkin_date: current.checkin_date,
      previous_payload_json: JSON.stringify(current),
      new_payload_json: JSON.stringify({ ...current, ...data }),
      edited_by: current.worker_id,
      edited_at: now
    });
    this.setStorageItem('workfit_checkin_revisions', revisions);

    const scores = [data.q1_mood, data.q2_work, data.q3_relationship, data.q4_health, data.q5_need_help, data.q6_overall];
    const average_score = calculateAverage(scores);

    const updated: DailyCheckin = {
      ...current,
      ...data,
      worker_id: data.worker_id?.trim() || current.worker_id,
      checkin_id: current.checkin_id,
      checkin_date: data.checkin_date || current.checkin_date,
      created_at: current.created_at,
      average_score,
      is_edited: true,
      message_tags: data.message_tags ? data.message_tags.join(',') : '',
      original_checkin_id: current.original_checkin_id || checkinId,
      updated_at: now
    };

    checkins[index] = updated;
    this.setStorageItem('workfit_checkins', checkins);
    if (updated.message_to_admin?.trim()) {
      const replies = this.getStorageItem<AdminReply[]>('workfit_replies', []);
      const replyIndex = replies.findIndex(r => r.checkin_id === checkinId && r.worker_message?.trim() && !r.reply_text?.trim());
      if (replyIndex >= 0) {
        replies[replyIndex] = {
          ...replies[replyIndex],
          worker_id: replies[replyIndex].worker_id || updated.worker_id,
          worker_message: updated.message_to_admin,
          message_status: '미답변',
          updated_at: now
        };
      } else {
        replies.push({
          reply_id: `RPL-${Date.now()}`,
          checkin_id: checkinId,
          worker_id: updated.worker_id,
          admin_id: '',
          worker_message: updated.message_to_admin,
          reply_text: '',
          message_status: '미답변',
          worker_read_at: '',
          created_at: now,
          updated_at: now
        });
      }
      this.setStorageItem('workfit_replies', replies);
    }
    return updated;
  }

  async getCheckinRevisions(checkinId: string): Promise<CheckinRevision[]> {
    const revisions = this.getStorageItem<CheckinRevision[]>('workfit_checkin_revisions', []);
    return revisions.filter(r => r.checkin_id === checkinId);
  }

  // 알림
  async getAlerts(filters?: AlertFilter): Promise<Alert[]> {
    let alerts = this.getStorageItem<Alert[]>('workfit_alerts', []);
    if (filters) {
      if (filters.status) alerts = alerts.filter(a => a.status === filters.status);
      if (filters.worker_id) alerts = alerts.filter(a => a.worker_id === filters.worker_id);
    }
    return alerts;
  }

  async updateAlertStatus(alertId: string, status: 'acknowledged' | 'resolved', memo?: string): Promise<void> {
    const alerts = this.getStorageItem<Alert[]>('workfit_alerts', []);
    const index = alerts.findIndex(a => a.alert_id === alertId);
    if (index === -1) throw new Error('알림을 찾을 수 없습니다.');
    alerts[index] = {
      ...alerts[index],
      status,
      admin_memo: memo || alerts[index].admin_memo,
      resolved_at: status === 'resolved' ? new Date().toISOString() : alerts[index].resolved_at
    };
    this.setStorageItem('workfit_alerts', alerts);
  }

  async resolveAttentionAlerts(data: ResolveAttentionInput): Promise<void> {
    const now = new Date().toISOString();
    const alerts = this.getStorageItem<Alert[]>('workfit_alerts', []);
    const updated = alerts.map((alert) => {
      const followUpRequired = String(alert.follow_up_required || '').toLowerCase() === 'true' || alert.follow_up_required === true;
      const isActive = alert.status === 'new' || alert.status === 'acknowledged' || alert.status === 'monitoring' || followUpRequired;
      if (alert.worker_id !== data.worker_id || !isActive || alert.status === 'resolved') return alert;
      return {
        ...alert,
        status: 'resolved' as const,
        resolved_at: now,
        resolved_by: data.admin_id,
        resolution_memo: data.resolution_memo || '',
        follow_up_required: false,
        admin_memo: data.resolution_memo || alert.admin_memo,
      };
    });
    this.setStorageItem('workfit_alerts', updated);
  }

  async markAttentionMonitoring(data: MarkAttentionMonitoringInput): Promise<void> {
    const alerts = this.getStorageItem<Alert[]>('workfit_alerts', []);
    const updated = alerts.map((alert) => {
      const followUpRequired = String(alert.follow_up_required || '').toLowerCase() === 'true' || alert.follow_up_required === true;
      const isActive = alert.status === 'new' || alert.status === 'acknowledged' || alert.status === 'monitoring' || followUpRequired;
      if (alert.worker_id !== data.worker_id || !isActive || alert.status === 'resolved') return alert;
      return {
        ...alert,
        status: 'monitoring' as const,
        follow_up_required: true,
        follow_up_memo: data.follow_up_memo || '',
        admin_memo: data.follow_up_memo || alert.admin_memo,
      };
    });
    this.setStorageItem('workfit_alerts', updated);
  }

  // 답변
  async getReplies(workerId: string): Promise<AdminReply[]> {
    const replies = this.getStorageItem<AdminReply[]>('workfit_replies', []);
    return workerId.trim() ? replies.filter(r => r.worker_id === workerId) : replies;
  }

  async getUnrepliedMessages(): Promise<AdminReply[]> {
    const replies = this.getStorageItem<AdminReply[]>('workfit_replies', []);
    return replies.filter(r =>
      r.worker_message &&
      r.worker_message.trim() !== '' &&
      (!r.reply_text || r.reply_text.trim() === '')
    );
  }

  async createReply(checkinId: string, adminId: string, replyText: string): Promise<AdminReply> {
    const replies = this.getStorageItem<AdminReply[]>('workfit_replies', []);
    const index = replies.findIndex(r => r.checkin_id === checkinId);
    const now = new Date().toISOString();

    if (index !== -1) {
      const updated: AdminReply = {
        ...replies[index],
        admin_id: adminId,
        reply_text: replyText,
        message_status: '답변완료',
        updated_at: now
      };
      replies[index] = updated;
      this.setStorageItem('workfit_replies', replies);

      // 연관된 알림들도 자동 해결 완료 처리
      const alerts = this.getStorageItem<Alert[]>('workfit_alerts', []);
      const updatedAlerts = alerts.map(a => {
        if (a.checkin_id === checkinId && (a.status === 'new' || a.status === 'acknowledged')) {
          return {
            ...a,
            status: 'resolved' as const,
            resolved_at: now,
            admin_memo: '답변 등록으로 자동 해결'
          };
        }
        return a;
      });
      this.setStorageItem('workfit_alerts', updatedAlerts);

      return updated;
    } else {
      // 신규 추가 시
      const checkins = this.getStorageItem<DailyCheckin[]>('workfit_checkins', []);
      const checkin = checkins.find(c => c.checkin_id === checkinId);
      if (!checkin) throw new Error('해당 체크인이 존재하지 않습니다.');

      const newReply: AdminReply = {
        reply_id: `RPL-${Date.now()}`,
        checkin_id: checkinId,
        worker_id: checkin.worker_id,
        admin_id: adminId,
        worker_message: checkin.message_to_admin || '',
        reply_text: replyText,
        message_status: '답변완료',
        worker_read_at: '',
        created_at: now,
        updated_at: now
      };
      replies.push(newReply);
      this.setStorageItem('workfit_replies', replies);
      return newReply;
    }
  }

  async markReplyRead(replyId: string): Promise<void> {
    const replies = this.getStorageItem<AdminReply[]>('workfit_replies', []);
    const index = replies.findIndex(r => r.reply_id === replyId);
    if (index === -1) throw new Error('답변을 찾을 수 없습니다.');
    replies[index] = {
      ...replies[index],
      message_status: '취업자읽음',
      worker_read_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    this.setStorageItem('workfit_replies', replies);
  }

  // 공지
  async getNotices(activeOnly?: boolean): Promise<Notice[]> {
    let notices = this.getStorageItem<Notice[]>('workfit_notices', []);
    if (activeOnly) {
      const today = getTodayString();
      notices = notices.filter(n => n.is_visible && n.start_date <= today && n.end_date >= today);
    }
    return notices;
  }

  async createNotice(data: NoticeInput, adminId: string): Promise<Notice> {
    const notices = this.getStorageItem<Notice[]>('workfit_notices', []);
    const nextId = `N-${Date.now()}`;
    const now = new Date().toISOString();
    const newNotice: Notice = {
      ...data,
      notice_id: nextId,
      created_by: adminId,
      created_at: now,
      updated_at: now
    };
    notices.push(newNotice);
    this.setStorageItem('workfit_notices', notices);
    return newNotice;
  }

  async updateNotice(noticeId: string, data: Partial<NoticeInput>): Promise<Notice> {
    const notices = this.getStorageItem<Notice[]>('workfit_notices', []);
    const index = notices.findIndex(n => n.notice_id === noticeId);
    if (index === -1) throw new Error('공지를 찾을 수 없습니다.');
    const updated: Notice = {
      ...notices[index],
      ...data,
      updated_at: new Date().toISOString()
    };
    notices[index] = updated;
    this.setStorageItem('workfit_notices', notices);
    return updated;
  }

  async getNoticeReads(noticeId: string): Promise<NoticeRead[]> {
    const reads = this.getStorageItem<NoticeRead[]>('workfit_notice_reads', []);
    return noticeId.trim() ? reads.filter(r => r.notice_id === noticeId) : reads;
  }

  async recordNoticeRead(noticeId: string, userId: string, userType: string): Promise<void> {
    const reads = this.getStorageItem<NoticeRead[]>('workfit_notice_reads', []);
    const already = reads.some(r => r.notice_id === noticeId && r.user_id === userId);
    if (already) return;

    reads.push({
      read_id: `NR-${Date.now()}`,
      notice_id: noticeId,
      user_id: userId,
      user_type: userType as any,
      read_at: new Date().toISOString()
    });
    this.setStorageItem('workfit_notice_reads', reads);
  }

  // 상담 기록
  async getAdminNotes(workerId: string): Promise<AdminNote[]> {
    const notes = this.getStorageItem<AdminNote[]>('workfit_admin_notes', []);
    return notes.filter(n => n.worker_id === workerId);
  }

  async createAdminNote(data: AdminNoteInput, adminId: string): Promise<AdminNote> {
    const notes = this.getStorageItem<AdminNote[]>('workfit_admin_notes', []);
    const nextId = `AN-${Date.now()}`;
    const now = new Date().toISOString();
    const newNote: AdminNote = {
      ...data,
      note_id: nextId,
      admin_id: adminId,
      note_date: data.note_date || getTodayString(),
      created_at: now,
      updated_at: now
    };
    notes.push(newNote);
    this.setStorageItem('workfit_admin_notes', notes);
    return newNote;
  }

  // 대시보드
  async getDashboardData(): Promise<DashboardData> {
    const today = getTodayString();
    const workers = await this.getWorkers();
    const eligibleWorkersToday = workers.filter(w => w.status === 'active' && w.employment_status === '취업 중' && isWorkerEligibleForStatsOnDate(w, today));
    const totalWorkers = eligibleWorkersToday.length;

    const checkins = this.getStorageItem<DailyCheckin[]>('workfit_checkins', []);
    const todayCheckins = checkins.filter(c => c.checkin_date === today && eligibleWorkersToday.some(w => w.worker_id === c.worker_id));
    const todayCheckedCount = todayCheckins.length;
    const todayUncheckedCount = Math.max(0, totalWorkers - todayCheckedCount);

    const alerts = this.getStorageItem<Alert[]>('workfit_alerts', []);
    const todayAlertCount = alerts.filter(a => a.alert_date === today).length;
    const unresolvedAlerts = alerts.filter(a => {
      const followUpRequired = String(a.follow_up_required || '').toLowerCase() === 'true' || a.follow_up_required === true;
      return a.status !== 'resolved' && (a.status === 'new' || a.status === 'acknowledged' || a.status === 'monitoring' || followUpRequired);
    });

    const replies = this.getStorageItem<AdminReply[]>('workfit_replies', []);
    const unanswered = replies.filter(r => r.worker_message && (!r.reply_text || r.reply_text.trim() === ''));

    const checkedIds = new Set(todayCheckins.map(c => c.worker_id));
    const uncheckedWorkers = eligibleWorkersToday.filter(w => !checkedIds.has(w.worker_id));

    const notices = this.getStorageItem<Notice[]>('workfit_notices', []);
    const activeNotices = notices.filter(n => n.is_visible && n.start_date <= today && n.end_date >= today);
    const reads = this.getStorageItem<NoticeRead[]>('workfit_notice_reads', []);
    const unreadNoticeCount = eligibleWorkersToday.filter(w => {
      return activeNotices.some(n => {
        const read = reads.some(r => r.user_id === w.worker_id && r.notice_id === n.notice_id);
        return !read;
      });
    }).length;

    const workerNames = workers.reduce((acc, w) => {
      acc[w.worker_id] = w.name;
      return acc;
    }, {} as Record<string, string>);

    // attentionWorkers 생성
    const unresolvedAlertsGrouped: Record<string, Alert[]> = unresolvedAlerts.reduce((acc, a) => {
      if (!acc[a.worker_id]) acc[a.worker_id] = [];
      acc[a.worker_id].push(a);
      return acc;
    }, {} as Record<string, Alert[]>);

    const attentionWorkers: AttentionWorkerItem[] = Object.keys(unresolvedAlertsGrouped).map(wId => {
      const workerAlerts = unresolvedAlertsGrouped[wId];
      // 가장 최근 알림순 정렬
      workerAlerts.sort((a, b) => b.alert_date.localeCompare(a.alert_date));
      const latestAlert = workerAlerts[0];
      const workerInfo = workers.find(w => w.worker_id === wId);

      return {
        worker_id: wId,
        worker_name: workerInfo ? workerInfo.name : '알 수 없음',
        current_company: workerInfo ? workerInfo.current_company : '알 수 없음',
        job_type: workerInfo ? workerInfo.job_type : '',
        alert_count: workerAlerts.length,
        latest_alert_type: latestAlert.alert_type,
        latest_alert_message: this.getAlertReason(latestAlert.alert_type),
        latest_alert_date: latestAlert.alert_date,
        latest_checkin_id: latestAlert.checkin_id
      };
    });

    // unansweredMessages 생성
    const unansweredMessages: DashboardMessageItem[] = unanswered.map(r => {
      const workerInfo = workers.find(w => w.worker_id === r.worker_id);
      const checkinInfo = checkins.find(c => c.checkin_id === r.checkin_id);

      return {
        reply_id: r.reply_id,
        checkin_id: r.checkin_id,
        worker_id: r.worker_id,
        worker_name: workerInfo ? workerInfo.name : '알 수 없음',
        current_company: workerInfo ? workerInfo.current_company : '알 수 없음',
        checkin_date: checkinInfo ? checkinInfo.checkin_date : '',
        worker_message: r.worker_message || '',
        message_tags: checkinInfo ? (checkinInfo.message_tags || '') : '',
        message_status: r.message_status,
        created_at: r.created_at
      };
    });

    // uncheckedWorkers 생성
    const uncheckedWorkersSummary: WorkerSummaryItem[] = uncheckedWorkers.map(w => ({
      worker_id: w.worker_id,
      name: w.name,
      current_company: w.current_company,
      job_type: w.job_type
    }));

    const recentWorkers: WorkerSummaryItem[] = [...workers]
      .sort((a, b) => String(b.updated_at || b.created_at || '').localeCompare(String(a.updated_at || a.created_at || '')))
      .slice(0, 5)
      .map(w => ({
        worker_id: w.worker_id,
        name: w.name,
        current_company: w.current_company,
        job_type: w.job_type,
        employment_start_date: w.employment_start_date,
        employment_status: w.employment_status,
        created_at: w.created_at,
        updated_at: w.updated_at,
      }));

    return {
      totalWorkers,
      todayCheckedCount,
      todayUncheckedCount,
      todayAlertCount,
      unresolvedAlertCount: unresolvedAlerts.length,
      unansweredMessageCount: unanswered.length,
      unreadNoticeCount,
      pendingSignupRequestCount: this.getStorageItem<WorkerSignupRequest[]>('workfit_worker_signup_requests', []).filter(r => r.status === 'pending').length,
      attentionWorkers,
      unansweredMessages: unansweredMessages.slice(0, 10),
      recentAlerts: unresolvedAlerts.slice(0, 10),
      uncheckedWorkers: uncheckedWorkersSummary,
      recentWorkers,
      workerNames,
      todayCheckins
    };
  }

  // 헬퍼
  private getAlertReason(type: string): string {
    const reasons: Record<string, string> = {
      score_drop: '전일 대비 평균점수 2점 이상 하락',
      low_score: '오늘 평균점수 2.5 이하',
      consecutive_low: '2일 연속 평균점수 2.5 이하',
      consecutive_drop: '3일 연속 점수 하락',
      need_help: '도움 필요 항목 낮은 점수',
      has_message: '관리자에게 전할 메시지 있음',
    };
    return reasons[type] || '기타 주의 사항 발생';
  }

  async getMonthlyReport(year: string, month: string): Promise<MonthlyReportData> {
    const now = new Date();
    const targetYear = year || String(now.getFullYear());
    const parsedMonth = Number.parseInt(month || String(now.getMonth() + 1), 10);
    const targetMonth = String(Number.isFinite(parsedMonth) ? parsedMonth : now.getMonth() + 1).padStart(2, '0');
    const monthPrefix = `${targetYear}-${targetMonth}`;
    const daysInMonth = new Date(Number(targetYear), Number(targetMonth), 0).getDate();

    const workers = await this.getWorkers();
    const currentWorkers = workers.filter(w => w.status === 'active' && w.employment_status === '취업 중');
    const checkins = this.getStorageItem<DailyCheckin[]>('workfit_checkins', []);
    const alerts = this.getStorageItem<Alert[]>('workfit_alerts', []);
    const replies = this.getStorageItem<AdminReply[]>('workfit_replies', []);
    const notes = this.getStorageItem<AdminNote[]>('workfit_admin_notes', []);

    const normalizeDate = (value: string) => {
      if (!value) return '';
      return value.includes('T') ? value.split('T')[0] : value;
    };
    const round1 = (value: number) => Math.round(value * 10) / 10;
    const average = (values: number[]) => values.length > 0 ? round1(values.reduce((sum, value) => sum + value, 0) / values.length) : 0;
    const checkinDateById = new Map(checkins.map(c => [c.checkin_id, normalizeDate(c.checkin_date)]));
    const getReplyDate = (reply: AdminReply) => normalizeDate(checkinDateById.get(reply.checkin_id) || reply.created_at || reply.updated_at);

    const workerById = new Map(workers.map(w => [w.worker_id, w]));
    const workersMissingEmploymentStartDate = currentWorkers.filter(w => !w.employment_start_date).length;
    const monthlyCheckins = checkins.filter(c => {
      const date = normalizeDate(c.checkin_date);
      const worker = workerById.get(c.worker_id);
      return date.startsWith(monthPrefix) && !!worker && isWorkerEligibleForStatsOnDate(worker, date);
    });
    const previousMonthDate = new Date(Number(targetYear), Number(targetMonth) - 2, 1);
    const previousMonthPrefix = `${previousMonthDate.getFullYear()}-${String(previousMonthDate.getMonth() + 1).padStart(2, '0')}`;
    const previousMonthCheckins = checkins.filter(c => {
      const date = normalizeDate(c.checkin_date);
      const worker = workerById.get(c.worker_id);
      return date.startsWith(previousMonthPrefix) && !!worker && isWorkerEligibleForStatsOnDate(worker, date);
    });
    const monthlyAlerts = alerts.filter(a => normalizeDate(a.alert_date).startsWith(monthPrefix));
    const monthlyReplies = replies.filter(r => getReplyDate(r).startsWith(monthPrefix));
    const monthlyNotes = notes.filter(n => normalizeDate(n.note_date).startsWith(monthPrefix));
    const messageCheckins = monthlyCheckins.filter(c => Boolean(c.message_to_admin?.trim()));

    const currentWorkerCount = currentWorkers.length;
    const uniqueWorkers = new Set(monthlyCheckins.map(c => c.worker_id)).size;
    const totalCheckins = monthlyCheckins.length;
    const averageScore = average(monthlyCheckins.map(c => Number(c.average_score || 0)));
    const unansweredMessageCount = messageCheckins.filter(c => {
      const reply = replies.find(r => r.checkin_id === c.checkin_id);
      return !reply || reply.message_status === '미확인' || !reply.reply_text?.trim();
    }).length;

    const questionLabels = {
      q1_mood: '오늘 기분은 어땠어요?',
      q2_work: '오늘 일은 어땠어요?',
      q3_relationship: '직장에서 사람들과 잘 지냈어요?',
      q4_health: '몸은 괜찮았어요?',
      q5_need_help: '도움이 필요하다고 느꼈나요?',
      q6_overall: '오늘 하루는 전체적으로 어땠어요?',
    };
    type QuestionKey = keyof typeof questionLabels;
    const questionKeys = Object.keys(questionLabels) as QuestionKey[];
    const questionAverages = questionKeys.map(key => {
      const currentAverage = average(monthlyCheckins.map(c => Number(c[key] || 0)));
      const previousAverage = average(previousMonthCheckins.map(c => Number(c[key] || 0)));
      return {
        key,
        label: questionLabels[key],
        averageScore: currentAverage,
        ...(monthlyCheckins.length > 0 && previousMonthCheckins.length > 0
          ? { previousMonthDiff: round1(currentAverage - previousAverage) }
          : {}),
      };
    });

    const alertLabels = {
      score_drop: '전날 대비 2점 이상 하락',
      low_score: '평균 2.5 이하',
      consecutive_low: '2일 연속 낮은 점수',
      consecutive_drop: '3일 연속 하락',
      need_help: '도움 필요 문항 낮은 점수',
      has_message: '관리자에게 하고 싶은 말 작성',
    };
    const alertBreakdown = Object.entries(alertLabels).map(([type, label]) => ({
      type,
      label,
      count: monthlyAlerts.filter(a => a.alert_type === type).length,
    }));

    const dailyStats = Array.from({ length: daysInMonth }, (_, index) => {
      const day = String(index + 1).padStart(2, '0');
      const date = `${monthPrefix}-${day}`;
      const eligibleWorkers = workers.filter(w => isWorkerEligibleForStatsOnDate(w, date));
      const dayCheckins = monthlyCheckins.filter(c => normalizeDate(c.checkin_date) === date);
      const completedCount = new Set(dayCheckins.map(c => c.worker_id)).size;
      const dayMessageCheckins = dayCheckins.filter(c => Boolean(c.message_to_admin?.trim()));
      return {
        date,
        eligibleWorkerCount: eligibleWorkers.length,
        completedCount,
        missingCount: Math.max(0, eligibleWorkers.length - completedCount),
        averageScore: average(dayCheckins.map(c => Number(c.average_score || 0))),
        alertCount: monthlyAlerts.filter(a => normalizeDate(a.alert_date) === date).length,
        messageCount: new Set(dayMessageCheckins.map(c => c.worker_id)).size,
        unansweredCount: dayMessageCheckins.filter(c => {
          const reply = replies.find(r => r.checkin_id === c.checkin_id);
          return !reply || reply.message_status === '미확인' || !reply.reply_text?.trim();
        }).length,
      };
    });
    const eligibleWorkerDays = dailyStats.reduce((sum, day) => sum + day.eligibleWorkerCount, 0);
    const completionRate = eligibleWorkerDays === 0 ? 0 : Math.min(100, round1((totalCheckins / eligibleWorkerDays) * 100));

    const lowestQuestion = totalCheckins > 0
      ? [...questionAverages].sort((a, b) => a.averageScore - b.averageScore)[0]
      : null;

    const observations = [
      `${targetYear}년 ${targetMonth}월 일일 체크에 참여한 취업자는 총 ${uniqueWorkers}명입니다.`,
      `월간 체크 연인원은 총 ${totalCheckins}건입니다.`,
      `월간 응답 대상 연인원은 총 ${eligibleWorkerDays}명일입니다.`,
      `월간 평균 점수는 ${averageScore}점입니다.`,
      `위험 알림은 총 ${monthlyAlerts.length}건 발생했습니다.`,
    ];
    if (lowestQuestion) {
      observations.push(`가장 낮은 평균 점수를 보인 문항은 "${lowestQuestion.label}"입니다.`);
    }

    const supportNeeds: string[] = [];
    if (monthlyAlerts.length > 0) {
      supportNeeds.push('위험 알림이 발생한 취업자에 대한 개별 확인이 필요합니다.');
    }
    if (unansweredMessageCount > 0) {
      supportNeeds.push('미답변 메시지에 대한 관리자 확인 및 답변 처리가 필요합니다.');
    }
    if (lowestQuestion?.key === 'q4_health') {
      supportNeeds.push('건강 상태 관련 문항 점수가 낮아 근무 피로도와 건강 상태 확인이 필요합니다.');
    }
    if (lowestQuestion?.key === 'q3_relationship') {
      supportNeeds.push('직장 내 관계 적응에 대한 추가 확인이 필요합니다.');
    }
    if (supportNeeds.length === 0) {
      supportNeeds.push('이번 달에는 즉시 조치가 필요한 추가 지원 항목이 크게 확인되지 않았습니다.');
    }

    return {
      year: targetYear,
      month: targetMonth,
      currentWorkerCount,
      eligibleWorkerDays,
      workersMissingEmploymentStartDate,
      uniqueWorkers,
      totalCheckins,
      completionRate,
      averageScore,
      alertCount: monthlyAlerts.length,
      unansweredMessageCount,
      workerMessageCount: messageCheckins.length,
      adminReplyCount: monthlyReplies.filter(r => Boolean(r.reply_text?.trim())).length,
      workerReadReplyCount: monthlyReplies.filter(r => r.message_status === '취업자읽음').length,
      closedMessageCount: monthlyReplies.filter(r => r.message_status === '종결').length,
      adminNoteCount: monthlyNotes.length,
      dailyStats,
      questionAverages,
      alertBreakdown,
      observations,
      supportNeeds,
      generatedAt: new Date().toISOString(),
    };
  }

  async repairWorkerFields(dryRun = true): Promise<RepairWorkerFieldsResult> {
    const workers = this.getStorageItem<Worker[]>('workfit_workers', []);
    let count = 0;
    const repairedRows: RepairWorkerFieldsResult['repairedRows'] = [];
    const isoReg = /^\d{4}-\d{2}-\d{2}T/;
    
    const updated = workers.map((w, index) => {
      let changed = false;
      const copy = { ...w };
      const rowNum = index + 2;
      
      if (isoReg.test(copy.phone_optional || '')) {
        repairedRows.push({
          row: rowNum,
          worker_id: w.worker_id,
          name: w.name,
          field: '연락처 (phone_optional)',
          oldValue: copy.phone_optional,
          newValue: ''
        });
        copy.phone_optional = '';
        changed = true;
      }
      
      if (isoReg.test(copy.termination_reason || '')) {
        repairedRows.push({
          row: rowNum,
          worker_id: w.worker_id,
          name: w.name,
          field: '퇴사 사유 (termination_reason)',
          oldValue: copy.termination_reason || '',
          newValue: ''
        });
        copy.termination_reason = '';
        changed = true;
      }
      
      if (changed) {
        count++;
      }
      return copy;
    });

    if (!dryRun && count > 0) {
      this.setStorageItem('workfit_workers', updated);
    }

    return {
      dryRun,
      count: repairedRows.length,
      repairedRows
    };
  }

  async getPrivacyConsents(): Promise<PrivacyConsent[]> {
    return this.getStorageItem<PrivacyConsent[]>('workfit_privacy_consents', [])
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }

  async getWorkerHome(workerId: string): Promise<WorkerHomeData> {
    const [worker, todayCheckin, activeNotices, replies, todayAttendance] = await Promise.all([
      this.getWorker(workerId), this.getTodayCheckin(workerId), this.getNotices(true),
      this.getReplies(workerId), this.getTodayAttendance(workerId)
    ]);
    if (!worker) throw new Error('취업자 정보를 찾을 수 없습니다.');
    return { worker, todayCheckin, activeNotices, replies, todayAttendance };
  }

  async getAdminMessages(): Promise<AdminMessagesData> {
    const [workers, checkins, replies] = await Promise.all([
      this.getWorkers(), this.getCheckins(''), this.getReplies('')
    ]);
    const messageReplies = replies.filter((reply) => Boolean(reply.worker_message?.trim()));
    const workerIds = new Set(messageReplies.map((reply) => reply.worker_id));
    const checkinIds = new Set(messageReplies.map((reply) => reply.checkin_id));
    return {
      workers: workers.filter((worker) => workerIds.has(worker.worker_id)),
      checkins: checkins.filter((checkin) => checkinIds.has(checkin.checkin_id)),
      replies: messageReplies,
    };
  }

  async getWorkerDetail(workerId: string): Promise<WorkerDetailData> {
    const [worker, checkins, replies, adminNotes, alerts] = await Promise.all([
      this.getWorker(workerId), this.getCheckins(workerId), this.getReplies(workerId),
      this.getAdminNotes(workerId), this.getAlerts({ worker_id: workerId })
    ]);
    if (!worker) throw new Error('취업자를 찾을 수 없습니다.');
    return { worker, checkins, replies, adminNotes, alerts };
  }

  async getTodayAttendance(workerId: string): Promise<AttendanceRecord | null> {
    const today = getTodayString();
    return this.getStorageItem<AttendanceRecord[]>('workfit_attendance_logs', [])
      .find((record) => record.worker_id === workerId && record.work_date === today) || null;
  }

  async getAttendance(workerId?: string, month?: string, date?: string): Promise<AttendanceRecord[]> {
    let records = this.getStorageItem<AttendanceRecord[]>('workfit_attendance_logs', []);
    if (workerId) records = records.filter((record) => record.worker_id === workerId);
    if (month) records = records.filter((record) => record.work_date.startsWith(month));
    if (date) records = records.filter((record) => record.work_date === date);
    return [...records].sort((a, b) => b.work_date.localeCompare(a.work_date));
  }

  async getAttendanceOverview(date: string, search = '', status = 'all'): Promise<AttendanceOverviewData> {
    const [workers, records] = await Promise.all([this.getWorkers(), this.getAttendance(undefined, undefined, date)]);
    const recordByWorker = new Map(records.map((record) => [record.worker_id, record]));
    const query = search.trim().toLowerCase();
    const allRows: AttendanceOverviewRow[] = workers
      .filter((worker) => worker.status === 'active' && worker.employment_status === '취업 중')
      .map((worker) => {
        const record = recordByWorker.get(worker.worker_id);
        return {
          worker_id: worker.worker_id,
          name: worker.name,
          current_company: worker.current_company,
          job_type: worker.job_type,
          attendance_id: record?.attendance_id,
          work_date: date,
          clock_in_at: record?.clock_in_at || '',
          clock_out_at: record?.clock_out_at || '',
          attendance_status: record?.attendance_status || '',
        };
      });
    const rows = allRows.filter((row) => {
      if (query && !row.name.toLowerCase().includes(query) && !row.current_company.toLowerCase().includes(query)) return false;
      if (status === 'clockedIn') return Boolean(row.clock_in_at);
      if (status === 'clockedOut') return Boolean(row.clock_out_at);
      if (status === 'missingIn') return !row.clock_in_at;
      if (status === 'missingOut') return Boolean(row.clock_in_at && !row.clock_out_at);
      return true;
    });
    return {
      date,
      totalWorkers: allRows.length,
      clockedInCount: allRows.filter((row) => row.clock_in_at).length,
      clockedOutCount: allRows.filter((row) => row.clock_out_at).length,
      missingInCount: allRows.filter((row) => !row.clock_in_at).length,
      missingOutCount: allRows.filter((row) => row.clock_in_at && !row.clock_out_at).length,
      rows,
    };
  }

  async clockIn(workerId: string, workerMemo?: string): Promise<AttendanceRecord> {
    const existing = await this.getTodayAttendance(workerId);
    if (existing) return existing;
    const records = this.getStorageItem<AttendanceRecord[]>('workfit_attendance_logs', []);
    const now = new Date().toISOString();
    const record: AttendanceRecord = {
      attendance_id: `AT-${Date.now()}`, worker_id: workerId, work_date: getTodayString(),
      clock_in_at: now, clock_out_at: '', attendance_status: '출근', worker_memo: workerMemo || '',
      modified_by: '', modification_reason: '', created_at: now, updated_at: now,
    };
    records.push(record);
    this.setStorageItem('workfit_attendance_logs', records);
    return record;
  }

  async clockOut(workerId: string, workerMemo?: string): Promise<AttendanceRecord> {
    const records = this.getStorageItem<AttendanceRecord[]>('workfit_attendance_logs', []);
    const index = records.findIndex((record) => record.worker_id === workerId && record.work_date === getTodayString());
    if (index < 0) throw new Error('먼저 출근을 기록해 주세요.');
    if (records[index].clock_out_at) return records[index];
    records[index] = { ...records[index], clock_out_at: new Date().toISOString(), attendance_status: '퇴근완료', worker_memo: workerMemo || records[index].worker_memo, updated_at: new Date().toISOString() };
    this.setStorageItem('workfit_attendance_logs', records);
    return records[index];
  }

  async updateAttendance(attendanceId: string, data: AttendanceUpdateInput): Promise<AttendanceRecord> {
    const records = this.getStorageItem<AttendanceRecord[]>('workfit_attendance_logs', []);
    const index = records.findIndex((record) => record.attendance_id === attendanceId);
    if (index < 0) throw new Error('출퇴근 기록을 찾을 수 없습니다.');
    const current = records[index];
    records[index] = { ...current, ...data, attendance_id: current.attendance_id, worker_id: current.worker_id, work_date: current.work_date, created_at: current.created_at, attendance_status: '관리자수정', updated_at: new Date().toISOString() };
    this.setStorageItem('workfit_attendance_logs', records);
    return records[index];
  }
}
